<script setup>
/**
 * One policy as a row, above four of them (prototype `polRow`): the renewal
 * dates line up in a column that can be read down. On a phone the column moves
 * under the number rather than disappearing — each policy still says how near
 * its renewal is. Home's Renews soon (XN-4566b) passes `detail` — the monthly
 * premium, or '' for none — in place of the number line.
 */
import { computed } from 'vue';
import { RouterLink } from 'vue-router';
import IconChevronRight from '../icons/IconChevronRight.vue';
import PolicyLineIcon from '../policyLines/PolicyLineIcon.vue';
import { money, numberLine, policyTitle, renewal, rowLine } from '../../helpers/coverage';

const props = defineProps({
  policy: { type: Object, required: true },
  now: { type: Date, required: true },
  detail: { type: String, default: undefined },
});
const when = computed(() => renewal(props.policy, props.now));
const column = computed(() => rowLine(when.value));
const color = computed(() => props.policy.line?.color || '#867D8D');
const premium = computed(() => {
  const amount = money(props.policy.monthly_premium, props.policy.currency);

  return amount ? `${amount}/mo` : '';
});
const second = computed(() => props.detail ?? [numberLine(props.policy), premium.value].filter(Boolean).join(' · '));
</script>

<template>
  <RouterLink :to="`/hub/coverage/${policy.id}`" class="cov-row" data-testid="policy-row">
    <span class="cov-row__ico" :style="{ background: `${color}14`, color }" aria-hidden="true">
      <PolicyLineIcon :name="policy.line?.icon || 'shield'" :size="22" />
    </span>
    <span class="cov-row__t">
      <b>{{ policyTitle(policy) }}</b>
      <span v-if="second">{{ second }}</span>
    </span>
    <span class="cov-row__rn" data-testid="row-when">
      <b :class="{ 'cov-row__soon': when.soon }">{{ column.top }}</b>
      <span>{{ column.bottom }}</span>
    </span>
    <IconChevronRight color="currentColor" width="9" height="16" class="cov-row__chev" />
  </RouterLink>
</template>

<style scoped>
.cov-row {
  display: flex; align-items: center; gap: 14px; padding: 14px 18px; border-bottom: 1px solid var(--xl-color-border-subtle);
  color: inherit; text-decoration: none; transition: background .12s;
}
.cov-row:last-child { border-bottom: 0; }
.cov-row:hover { background: var(--xl-color-primary-container); }
.cov-row:focus-visible { outline: 2px solid var(--xl-color-primary); outline-offset: -2px; }
.cov-row__ico { width: 40px; height: 40px; flex: 0 0 40px; border-radius: 12px; display: grid; place-items: center; }
.cov-row__t { flex: 1; min-width: 0; }
.cov-row__t b { display: block; font-size: 15px; letter-spacing: -0.012em; overflow-wrap: anywhere; min-width: 0; }
.cov-row__t span { display: block; margin-top: 1px; font-size: 12.5px; color: var(--xl-color-outline); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.cov-row__rn { flex: 0 0 auto; text-align: right; }
.cov-row__rn b { display: block; font-size: 13.5px; font-weight: 600; }
.cov-row__rn b.cov-row__soon { color: var(--xl-color-warning); }
/* The prototype's faint grey is for headings; words that carry meaning need contrast. */
.cov-row__rn span { font-size: 12px; color: var(--xl-color-outline); }
.cov-row__chev { color: var(--xl-color-outline-variant); flex: 0 0 auto; }
@media (max-width: 620px) {
  .cov-row { flex-wrap: wrap; row-gap: 4px; }
  .cov-row__rn { order: 3; flex-basis: 100%; text-align: left; padding-left: 54px; display: flex; gap: 6px; align-items: baseline; }
}
</style>
